"use client";

// A real menu for a <select>, written by hand: this repo has neither shadcn nor Radix in apps/north.law, and adding a
// dependency for one field is not a trade we want (Jérémy, 18/09: "il faudrait afficher les rôles disponibles, mais
// avec un vrai dropdown ; je ne sais pas si on a du shadcn").
//
// It is an ENHANCEMENT, not a replacement. <SelectMenu htmlFor="role" /> renders nothing on the server and nothing on
// the first client render: it waits for the effect, then builds a button + listbox next to the native <select> it was
// pointed at and hides that <select> in CSS. So:
//   · without JavaScript — and for the first paint — the native control is the one on the page, fully usable;
//   · the <select> stays in the DOM, keeps its name, its options and its value, and every choice is written back to it
//     with a real `change` event, so React's own onChange runs and the submitted JSON is byte for byte what it was;
//   · nothing in the form's markup, validation or behaviour has to change to get the menu.
//
// Keyboard (WAI-ARIA APG, select-only combobox): closed — Down / Up / Enter / Space / Home / End open it, a printable
// character picks the matching option straight away; open — Down / Up move, Home / End jump, Enter / Space choose,
// Escape closes without choosing, Tab chooses and moves on, printable characters search. Focus never leaves the
// button: the active option is named by aria-activedescendant.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./select.css";

type Option = { value: string; label: string; disabled: boolean };
type Phase = "closed" | "open" | "closing";

/** Matches --v2-dur-hover in components/home-v2/tokens.css: the open / close fade. */
const DURATION = 180;
/** Matches .ds-select__menu's max-height and option height in select.css: only used to decide which way it opens. */
const MENU_MAX = 320;
const OPTION_HEIGHT = 40;

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Chevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function Tick() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 7.5l3 3 6-7" />
    </svg>
  );
}

export default function SelectMenu({ htmlFor }: { htmlFor: string }) {
  const uid = useId().replace(/:/g, "");
  const [mount, setMount] = useState<{ native: HTMLSelectElement; host: HTMLSpanElement } | null>(null);
  const [labelId, setLabelId] = useState<string | undefined>(undefined);
  const [value, setValue] = useState("");
  const [phase, setPhase] = useState<Phase>("closed");
  const [active, setActive] = useState(0);
  const [up, setUp] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const search = useRef({ text: "", at: 0 });
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ---- attach to the native control -------------------------------------------------------------------------------
  // Our own <span> is appended to the field, so React never owns it and never has to reconcile around it; the native
  // <select> and the field's chevron are hidden by a :has() rule in select.css, which only matches once that span is
  // there — no JavaScript, no span, and the native control is simply the control.
  useEffect(() => {
    const native = document.getElementById(htmlFor);
    if (!(native instanceof HTMLSelectElement)) return;
    const host = document.createElement("span");
    host.className = "ds-select";
    (native.parentElement ?? native).append(host);
    native.setAttribute("tabindex", "-1");
    native.setAttribute("aria-hidden", "true");
    setMount({ native, host });
    setValue(native.value);
    return () => {
      host.remove();
      native.removeAttribute("tabindex");
      native.removeAttribute("aria-hidden");
      setMount(null);
    };
  }, [htmlFor]);

  // The <label> points at a control that is no longer reachable: give it an id we can be named by, and make a click on
  // it focus the button, which is what a label does.
  useEffect(() => {
    const native = mount?.native;
    const label = native?.labels?.[0];
    if (!label) return;
    if (!label.id) label.id = `${htmlFor}-label`;
    setLabelId(label.id);
    const focus = (event: MouseEvent) => {
      event.preventDefault();
      buttonRef.current?.focus();
    };
    label.addEventListener("click", focus);
    return () => label.removeEventListener("click", focus);
  }, [mount, htmlFor]);

  // Anything else that writes to the control (React re-rendering it, a browser restoring a form) is heard here.
  useEffect(() => {
    const native = mount?.native;
    if (!native) return;
    const sync = () => setValue(native.value);
    native.addEventListener("change", sync);
    return () => native.removeEventListener("change", sync);
  }, [mount]);

  // The options ARE the native ones, read once: same labels, same values, same order, nothing to keep in step.
  const options = useMemo<Option[]>(
    () => (mount ? [...mount.native.options].map((o) => ({ value: o.value, label: o.text, disabled: o.disabled })) : []),
    [mount],
  );
  const selected = Math.max(0, options.findIndex((o) => o.value === value));
  const current = options[selected];

  const commit = useCallback(
    (index: number) => {
      const native = mount?.native;
      const option = options[index];
      if (!native || !option || option.disabled) return;
      if (native.value !== option.value) {
        native.value = option.value;
        // A <select> has no React value tracker: the plain `change` event is what React listens for, so the form's own
        // onChange runs and its state — the state the JSON body is built from — is updated exactly as a click would.
        native.dispatchEvent(new Event("change", { bubbles: true }));
      }
      setValue(option.value);
    },
    [mount, options],
  );

  const close = useCallback((restoreFocus: boolean) => {
    setPhase((was) => (was === "open" ? "closing" : was));
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPhase("closed"), reduced() ? 0 : DURATION);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  const open = useCallback(
    (index: number) => {
      clearTimeout(closeTimer.current);
      setActive(Math.max(0, Math.min(index, options.length - 1)));
      const box = buttonRef.current?.getBoundingClientRect();
      // Flip up only when there is genuinely no room below and more room above.
      const below = box ? window.innerHeight - box.bottom : Infinity;
      const above = box ? box.top : 0;
      const wanted = Math.min(MENU_MAX, options.length * OPTION_HEIGHT + 10);
      setUp(below < wanted && above > below);
      setPhase("open");
    },
    [options.length],
  );

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Pointer outside, or a resize that would leave the menu somewhere else: close, and leave the focus where the user
  // put it. Only a change of WIDTH counts as a resize — on a phone, scrolling hides the URL bar and fires one
  // resize per pixel of height, which would shut the menu as soon as it opened.
  useEffect(() => {
    if (phase !== "open") return;
    const width = window.innerWidth;
    const away = (event: PointerEvent) => {
      if (!mount?.host.contains(event.target as Node)) close(false);
    };
    const gone = () => {
      if (window.innerWidth !== width) close(false);
    };
    document.addEventListener("pointerdown", away, true);
    window.addEventListener("resize", gone);
    return () => {
      document.removeEventListener("pointerdown", away, true);
      window.removeEventListener("resize", gone);
    };
  }, [phase, mount, close]);

  // Keep the active option in view without moving the page.
  useEffect(() => {
    if (phase !== "open") return;
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [phase, active]);

  /** Type to search: the first option that starts with what has been typed in the last 500 ms. */
  const find = useCallback(
    (key: string, from: number) => {
      const now = Date.now();
      search.current.text = (now - search.current.at < 500 ? search.current.text : "") + key.toLowerCase();
      search.current.at = now;
      const text = search.current.text;
      // One repeated letter walks through the options that start with it, as a native <select> does.
      const single = text.length > 1 && [...text].every((c) => c === text[0]);
      const needle = single ? (text[0] ?? "") : text;
      const order = [...options.keys()].map((i) => (i + from + (single ? 1 : 0)) % options.length);
      return order.find((i) => !options[i]?.disabled && options[i]?.label.toLowerCase().startsWith(needle)) ?? -1;
    },
    [options],
  );

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const { key, altKey } = event;
    const last = options.length - 1;
    const step = (to: number) => {
      event.preventDefault();
      setActive(Math.max(0, Math.min(to, last)));
    };

    if (phase !== "open") {
      if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " " || key === "Home" || key === "End") {
        event.preventDefault();
        open(key === "Home" ? 0 : key === "End" ? last : key === "ArrowUp" ? Math.max(0, selected - 1) : key === "ArrowDown" && !altKey ? Math.min(last, selected + 1) : selected);
        return;
      }
      if (key.length === 1 && key !== " ") {
        const hit = find(key, selected);
        if (hit >= 0) {
          event.preventDefault();
          commit(hit);
        }
      }
      return;
    }

    switch (key) {
      case "ArrowDown":
        return step(altKey ? active : active + 1);
      case "ArrowUp":
        if (altKey) {
          event.preventDefault();
          commit(active);
          return close(true);
        }
        return step(active - 1);
      case "Home":
        return step(0);
      case "End":
        return step(last);
      case "PageDown":
        return step(active + 5);
      case "PageUp":
        return step(active - 5);
      case "Enter":
      case " ":
        event.preventDefault();
        commit(active);
        return close(true);
      case "Escape":
        event.preventDefault();
        return close(true);
      case "Tab":
        // Tab chooses, then leaves: the browser moves the focus on its own, so nothing is restored here.
        commit(active);
        return close(false);
      default:
        if (key.length === 1) {
          const hit = find(key, active);
          if (hit >= 0) step(hit);
        }
    }
  }

  if (!mount || !current) return null;

  const listId = `ds-select-${uid}`;
  const valueId = `${listId}-value`;
  const optionId = (i: number) => `${listId}-option-${i}`;

  return createPortal(
    <>
      <button
        ref={buttonRef}
        type="button"
        id={`${listId}-button`}
        className="ds-field__control ds-select__button"
        role="combobox"
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-expanded={phase === "open"}
        aria-activedescendant={phase === "open" ? optionId(active) : undefined}
        // Named by the field's label AND by what is chosen, so it is read as "Your role, Partner".
        aria-labelledby={[labelId, valueId].filter(Boolean).join(" ")}
        onClick={() => (phase === "open" ? close(true) : open(selected))}
        onKeyDown={onKeyDown}
        // The focus never leaves the button while the menu is used (an option's pointerdown is prevented), so losing
        // it means the window or another control took it: the menu has no reason to stay open behind that.
        onBlur={() => phase !== "closed" && close(false)}
      >
        <span id={valueId} className={`ds-select__value${current.value === "" ? " ds-select__value--empty" : ""}`}>
          {current.label}
        </span>
        <span className="ds-select__chevron" aria-hidden="true">
          <Chevron />
        </span>
      </button>
      {phase !== "closed" && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          className={`ds-select__menu${up ? " ds-select__menu--up" : ""}${phase === "closing" ? " ds-select__menu--closing" : ""}`}
          aria-labelledby={labelId}
          tabIndex={-1}
        >
          {options.map((option, i) => (
            <div
              key={option.value || `empty-${i}`}
              id={optionId(i)}
              role="option"
              className="ds-select__option"
              aria-selected={i === selected}
              aria-disabled={option.disabled || undefined}
              data-active={i === active}
              // The button keeps the focus: pressing the pointer here must not take it away.
              onPointerDown={(event) => event.preventDefault()}
              // pointermove, not pointerenter: enter also fires when the list moves under a pointer that has not
              // moved (a keyboard step scrolling the menu), and the mouse would then steal the active option back.
              onPointerMove={() => setActive(i)}
              onClick={() => {
                commit(i);
                close(true);
              }}
            >
              <span className="ds-select__label">{option.label}</span>
              <span className="ds-select__tick" aria-hidden="true">
                {i === selected && <Tick />}
              </span>
            </div>
          ))}
        </div>
      )}
    </>,
    mount.host,
  );
}
